import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Clock, ShieldCheck, Sparkles, Send } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { usePageTitle } from "@/hooks/usePageTitle";
import { Reveal, Chapter } from "@/components/Shared";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const POINT_ICONS = [Clock, Sparkles, ShieldCheck];

const DemoPage = () => {
  const { t } = useLanguage();
  usePageTitle(t.nav.demo);
  const d = t.demo;
  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    company: "",
    stores: "",
    message: "",
  });
  const [sending, setSending] = useState(false);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target ? e.target.value : e }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/demo-request`, form);
      toast.success(d.success);
      setForm({ first_name: "", last_name: "", email: "", phone: "", company: "", stores: "", message: "" });
    } catch (err) {
      toast.error(d.error);
    } finally {
      setSending(false);
    }
  };

  const field =
    "h-12 rounded-xl border-slate-200 bg-white text-sm text-slate-900 placeholder:text-slate-400 focus-visible:ring-blue-600";
  const label = "text-xs font-bold uppercase tracking-wider text-slate-500";

  return (
    <main data-testid="demo-page" className="pt-[76px]">
      <section className="relative overflow-hidden bg-gradient-to-b from-blue-50/70 via-white to-white py-20 lg:py-28">
        <div className="pointer-events-none absolute inset-0 bg-grid-light opacity-60 [mask-image:radial-gradient(ellipse_at_top,black_10%,transparent_70%)]" />
        <div className="relative mx-auto grid max-w-7xl gap-14 px-5 lg:grid-cols-[1fr_1.1fr] lg:px-8">
          <Reveal>
            <Chapter index="01" label={d.eyebrow} />
            <h1 className="max-w-lg font-display text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl lg:text-5xl text-balance">
              {d.title}
            </h1>
            <p className="mt-5 max-w-md text-base leading-relaxed text-slate-600 sm:text-lg">{d.subtitle}</p>
            <ul className="mt-9 space-y-4">
              {d.points.map((point, i) => {
                const Icon = POINT_ICONS[i];
                return (
                  <li key={point} className="flex items-center gap-3.5">
                    <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-50">
                      <Icon className="h-5 w-5 text-emerald-500" />
                    </span>
                    <span className="text-sm font-semibold text-slate-700">{point}</span>
                  </li>
                );
              })}
            </ul>
            <div className="mt-12 rounded-2xl border border-slate-100 bg-white p-6 shadow-sm">
              <p className="font-hand text-2xl font-semibold text-slate-700">
                « {t.testimonial.quote} »
              </p>
              <p className="mt-3 text-xs font-bold text-slate-900">
                {t.testimonial.name} <span className="font-medium text-slate-500">— {t.testimonial.role}</span>
              </p>
            </div>
          </Reveal>

          <Reveal delay={0.15}>
            <form
              onSubmit={onSubmit}
              data-testid="demo-form"
              className="rounded-2xl border border-slate-200 bg-white p-7 shadow-[0_32px_64px_-24px_rgba(10,15,29,0.15)] lg:p-9"
            >
              <div className="grid gap-5 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="first_name" className={label}>
                    {d.form.firstName}
                  </Label>
                  <Input
                    id="first_name"
                    data-testid="demo-firstname-input"
                    required
                    value={form.first_name}
                    onChange={set("first_name")}
                    className={field}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="last_name" className={label}>
                    {d.form.lastName}
                  </Label>
                  <Input
                    id="last_name"
                    data-testid="demo-lastname-input"
                    required
                    value={form.last_name}
                    onChange={set("last_name")}
                    className={field}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email" className={label}>
                    {d.form.email}
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    data-testid="demo-email-input"
                    required
                    value={form.email}
                    onChange={set("email")}
                    className={field}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone" className={label}>
                    {d.form.phone}
                  </Label>
                  <Input
                    id="phone"
                    type="tel"
                    data-testid="demo-phone-input"
                    required
                    value={form.phone}
                    onChange={set("phone")}
                    className={field}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="company" className={label}>
                    {d.form.company}
                  </Label>
                  <Input
                    id="company"
                    data-testid="demo-company-input"
                    required
                    value={form.company}
                    onChange={set("company")}
                    className={field}
                  />
                </div>
                <div className="space-y-2">
                  <Label className={label}>{d.form.stores}</Label>
                  <Select required value={form.stores} onValueChange={set("stores")}>
                    <SelectTrigger data-testid="demo-stores-select" className={field}>
                      <SelectValue placeholder={d.form.storesPlaceholder} />
                    </SelectTrigger>
                    <SelectContent>
                      {d.form.storeOptions.map((opt) => (
                        <SelectItem key={opt} value={opt} data-testid={`demo-stores-option-${opt}`}>
                          {opt}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2 sm:col-span-2">
                  <Label htmlFor="message" className={label}>
                    {d.form.message}
                  </Label>
                  <Textarea
                    id="message"
                    data-testid="demo-message-input"
                    rows={4}
                    value={form.message}
                    onChange={set("message")}
                    placeholder={d.form.messagePlaceholder}
                    className="rounded-xl border-slate-200 bg-white text-sm text-slate-900 placeholder:text-slate-400 focus-visible:ring-blue-600"
                  />
                </div>
              </div>
              <button
                type="submit"
                disabled={sending}
                data-testid="demo-submit-button"
                className="group mt-7 flex w-full items-center justify-center gap-2 rounded-xl bg-blue-600 px-6 py-4 text-sm font-bold text-white shadow-lg shadow-blue-600/25 transition-all duration-300 hover:bg-blue-700 disabled:opacity-60"
              >
                {sending ? d.form.sending : d.form.submit}
                <Send className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-0.5" />
              </button>
            </form>
          </Reveal>
        </div>
      </section>
    </main>
  );
};

export default DemoPage;
